import React, { useState, useEffect } from 'react';
import { useConfigurator } from '../../context/ConfiguratorContext';
import { Configurator } from '../../types';
import translationInstance from '../../utils/translations';
import { getAssetPath } from '../../utils/assetPath';
import togglePaneIcon from '../../assets/images/toggle-pane.png';
import backIcon from '../../assets/images/Icon.png';
import hamburger from '../../assets/images/hamburger.png';
import './ConfiguratorSelector.scss';

// Flag to control if selector starts at full width
const START_FULL_W_SELECTOR = true;

// Track if user has made an explicit selection
let userHasSelectedConfigurator = false;

const ConfiguratorSelector: React.FC = () => {
  const { availableConfigurators, selectedConfiguratorId, setSelectedConfiguratorId, isLoading, selectedLanguage, updateUrlWithConfiguratorId } = useConfigurator();
  const [isHidden, setIsHidden] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredConfigurators, setFilteredConfigurators] = useState(availableConfigurators);
  const [isFullWidthInitial, setIsFullWidthInitial] = useState(false);
  
  // Initialize full-width state based on whether a configurator is explicitly selected by user
  useEffect(() => {
    // Check if configurator ID is in URL parameters (explicit selection)
    const urlParams = new URLSearchParams(window.location.search);
    const configIdInUrl = urlParams.get('configurator');
    
    // If URL already has a configurator parameter, consider it an explicit selection
    if (configIdInUrl) {
      userHasSelectedConfigurator = true;
    }
    
    // Only go full-width if the flag is enabled AND user hasn't made an explicit selection
    setIsFullWidthInitial(START_FULL_W_SELECTOR && !userHasSelectedConfigurator);
  }, []);

  // Get translation function
  const t = translationInstance.t.bind(translationInstance);

  // Make sure we're using the current language
  translationInstance.setLanguage(selectedLanguage);

  // Filter configurators when search term or available configurators change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredConfigurators(availableConfigurators);
    } else {
      const filtered = availableConfigurators.filter(configurator =>
        configurator.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredConfigurators(filtered);
    }
  }, [searchTerm, availableConfigurators]);

  const handleConfiguratorClick = (configurator: Configurator) => {
    if (!isLoading) {
      // Handle clicking the already selected configurator when in full-width initial state
      if (configurator.id === selectedConfiguratorId && isFullWidthInitial) {
        setIsFullWidthInitial(false);
        userHasSelectedConfigurator = true;
        // On mobile, also hide the selector pane
        if (window.innerWidth <= 1024) {
          hideSelectorPane();
        }
        return;
      }

      if (configurator.id !== selectedConfiguratorId) {
        // Set the selected configurator ID which will update the URL via the effect in context
        setSelectedConfiguratorId(configurator.id);

        // Mark that user has made an explicit selection
        userHasSelectedConfigurator = true;

        // If we're in full-width initial state, exit it
        if (isFullWidthInitial) {
          setIsFullWidthInitial(false);
        }

        // Mobile-specific behavior: hide selector pane on mobile screens after selection
        if (window.innerWidth <= 1024) {
          hideSelectorPane();
        }
      }
    }
  };
  


  const toggleSelectorPane = () => {
    setIsHidden(!isHidden);
    // Legacy behavior often triggered events on toggle
    window.dispatchEvent(new CustomEvent('configuratorSelectorToggle', {
      detail: { isHidden: !isHidden }
    }));
  };

  const hideSelectorPane = () => {
    setIsHidden(true);
    window.dispatchEvent(new CustomEvent('configuratorSelectorToggle', {
      detail: { isHidden: true }
    }));
  };

  return (
    <>
      {/* Mobile show button - only visible on smaller screens */}
      <div onClick={() => setIsHidden(false)}>
        <div className='hamburger-icon'>
          <img src={hamburger} alt="menu" />
        </div>
      </div>

      <div className={`configurator-selector ${isHidden ? 'hidden' : ''} ${isFullWidthInitial ? 'full-width-initial' : ''}`}>
        <div className="configurator-selector-header">
          <div className='hide-selector-btn-wrap'>
            <span onClick={hideSelectorPane}>  <img src={backIcon} /> </span>
          </div>
          <div className='selector-header-text'>
            <h2>
              {t('virtenzo.configurator.products')}
              ({availableConfigurators.length})
            </h2>
          </div>
        </div>

        <div className="configurator-search-filter">
          <input
            type="text"
            className="configurator-search-input"
            placeholder={t('virtenzo.configurator.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="virtenzo-configurator-wrapper">
          <ul id="virtenzo-configurator-list" className="virtenzo-configurator-list">
            {filteredConfigurators.map((configurator) => (
              <li
                key={configurator.id}
                data-id={configurator.id}
                className={configurator.id === selectedConfiguratorId ? 'active' : ''}
                onClick={() => handleConfiguratorClick(configurator)}
              >
                <div
                  className="configurator-item-image"
                  style={{ backgroundImage: `url(${getAssetPath(`images/products-jpg/${configurator.id}.jpg`)})` }}
                ></div>
                <div className="configurator-item-content">
                  {configurator.name}

                </div>
              </li>
            ))}
          </ul>
        </div>


        <div className="toggle-selector-pane" id="toggle-selector-pane" onClick={toggleSelectorPane}>
          <img src={togglePaneIcon} alt="Toggle pane" />
        </div>
      </div>
    </>
  );
};

export default ConfiguratorSelector;
